import { useState } from 'react';
import { Camera } from 'lucide-react';
import { screenshotsEnabled } from '../../lib/features';
import { ScreenshotViewer } from './ScreenshotViewer';
import type { CompetitorFundraiser } from '../../types';

/**
 * Newest screenshot as a small thumbnail with a count; opens the viewer. When the thumbnail can't
 * load (its signed URL expired, or none was issued) it shows a camera icon with the count instead;
 * the viewer lists fresh URLs when it opens. Nothing when there are no screenshots.
 */
export const ScreenshotThumb = ({ fundraiser }: { fundraiser: CompetitorFundraiser }) => {
  const [open, setOpen] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const count = fundraiser.screenshotCount ?? 0;
  if (!screenshotsEnabled() || count === 0) return null;
  const url = fundraiser.latestScreenshotUrl;
  const showImage = !!url && url !== failedUrl;
  const label = `${count} screenshot${count === 1 ? '' : 's'}`;
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="relative flex-shrink-0"
      >
        {showImage ? (
          <>
            <img
              src={url}
              alt=""
              onError={() => setFailedUrl(url)}
              className="h-8 w-8 rounded object-cover border border-gray-200 dark:border-gray-700"
            />
            {count > 1 && (
              <span className="absolute -bottom-1 -right-1 rounded-full bg-gray-800 text-white text-[10px] leading-none px-1 py-0.5">{count}</span>
            )}
          </>
        ) : (
          <span data-testid="screenshot-thumb-fallback" className="h-8 inline-flex items-center gap-1 rounded border border-gray-200 dark:border-gray-700 px-1.5 text-xs text-gray-600 dark:text-gray-300">
            <Camera className="h-4 w-4" />
            {count}
          </span>
        )}
      </button>
      {open && <ScreenshotViewer fundraiserId={fundraiser.id} onClose={() => setOpen(false)} />}
    </>
  );
};
